import { useEffect } from 'react';
import type { Organization } from '../types/organization';
import { OrganizationStorageService } from '../services/storage/OrganizationStorageService';
import { organizationApi } from '../services/api/endpoints/organization';
import { useAuthStore } from '../stores/authStore';

// Ré-exporter le type pour la compatibilité avec les imports existants
export type { Organization } from '../types/organization';

/**
 * Hook pour accéder à l'organisation de l'utilisateur
 * 
 * IMPORTANT: L'organisation est maintenant récupérée via /auth/me dans authStore
 * Ce hook utilise l'organisation du contexte d'authentification
 * 
 * Workflow:
 * 1. Au login, authStore.initialize() appelle /auth/me
 * 2. /auth/me renvoie user + organization + businessUnit
 * 3. Ce hook lit organization depuis authStore
 * 4. Pour la mise à jour, on appelle /organization PATCH
 */
export function useOrganization() {
  // Récupérer l'organisation depuis le authStore (source de vérité)
  const { 
    organization, 
    loading, 
    isAuthenticated,
    setOrganization,
    syncStatus 
  } = useAuthStore();

  // Initialiser le service de stockage au montage (pour le cache local)
  useEffect(() => {
    const initStorage = async () => {
      try {
        await OrganizationStorageService.init();
        
        // Si l'organisation existe dans authStore, mettre en cache
        if (organization) {
          await OrganizationStorageService.saveOrganization(organization);
        }
      } catch (error) {
        console.error('❌ Erreur initialisation du stockage:', error);
      }
    };
    
    initStorage();
  }, [organization]);

  // Mettre à jour l'organisation via l'API
  const updateOrganization = async (data: Partial<Organization>) => {
    if (!organization) {
      throw new Error('Aucune organisation à mettre à jour');
    }
    
    try {
      console.log('🔄 Mise à jour de l\'organisation via API:', data);
      
      // Appel API pour mettre à jour l'organisation
      const response = await organizationApi.updateOrganization(data);
      console.log('📥 Réponse API updateOrganization:', response);
      
      if (response.success && response.data) {
        console.log('✅ Organisation mise à jour avec succès');
        
        // Mettre à jour le authStore (source de vérité)
        setOrganization(response.data);
        
        // Mettre à jour le cache IndexedDB
        await OrganizationStorageService.updateOrganization(data);
        
        return response.data;
      } else {
        console.error('❌ Échec de la mise à jour:', response.error);
        throw new Error(response.error || 'Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour:', error);
      
      // Fallback vers IndexedDB
      try {
        console.log('💾 Fallback: mise à jour dans IndexedDB uniquement');
        const updatedOrg = await OrganizationStorageService.updateOrganization(data);
        if (updatedOrg) {
          setOrganization(updatedOrg);
          return updatedOrg;
        }
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
      }
      
      throw error;
    }
  };

  return {
    organization,
    loading,
    isAuthenticated,
    syncStatus,
    updateOrganization
  };
}